// Файл PDFViewer.js

/**
 * Модуль «PDFViewer» содержит логику, относящуюся к разделу «PDFViewer».
 * Он объединяет связанные обработчики, данные и элементы интерфейса.
 * Соседние модули получают готовое поведение просмотра документа.
 * Такое разделение упрощает сопровождение, тестирование и повторное
 * использование этой части кода.
 */

import './promise_with_resolvers_polyfill';

import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import { apiService } from '../../services/api';
import LoadingIndicator from '../../shared_code/loading';

import './PDFViewer.css';



pdfjs.GlobalWorkerOptions.workerSrc = (
    `https://unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/`
    + 'pdf.worker.min.mjs'
);

const PDF_OPTIONS = {
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: (
        `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`
    ),
};

const PAGES_BATCH_SIZE = 1;


const is_android_webview = () => {
    if (typeof window !== 'undefined'
        && (window.PotaleAndroid || window.Android)) {
        return true;
    }

    if (typeof navigator === 'undefined') {
        return false;
    }

    const user_agent = String(navigator.userAgent || '').toLowerCase();
    const is_android = user_agent.includes('android');

    return (
        user_agent.includes('; wv)')
        || user_agent.includes(' webview')
        || (
            is_android
            && user_agent.includes('version/')
            && user_agent.includes('chrome/')
        )
    );
};


const PDFViewer = ({
    pdf_path,
    loadBlob = null,
    webview_image_fallback = false,
}) => {
    const [numPages, setNumPages] = useState(null);
    const [pageWidth, setPageWidth] = useState(900);
    const [blobUrl, setBlobUrl] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [visible_pages_count, set_visible_pages_count] = useState(0);
    const blobUrlRef = useRef('');
    const loadBlobRef = useRef(loadBlob);
    loadBlobRef.current = loadBlob;
    const hasCustomLoader = typeof loadBlob === 'function';
    const use_image_fallback = Boolean(
        webview_image_fallback
        && !hasCustomLoader
        && pdf_path
        && is_android_webview()
    );

    useEffect(() => {
        if (!pdf_path && !hasCustomLoader) {
            return;
        }

        setIsLoading(true);
        setError('');
        setBlobUrl('');
        setNumPages(null);
        set_visible_pages_count(0);

        Promise.resolve()
            .then(() => (
                hasCustomLoader
                    ? loadBlobRef.current()
                    : apiService.get_file_blob_POST(
                        'get_pdf',
                        {
                            path: pdf_path,
                            ...(use_image_fallback
                                ? { render: 'png' }
                                : {}),
                        },
                    )
            ))
            .then((blob) => {
                if (
                    use_image_fallback
                    && blob.type
                    && !blob.type.startsWith('image/')
                ) {
                    throw new Error(
                        `Ожидалось изображение, а пришло: ${blob.type}`
                    );
                }

                if (
                    !use_image_fallback
                    && blob.type
                    && !blob.type.includes('pdf')
                ) {
                    throw new Error(`Ожидался PDF, а пришло: ${blob.type}`);
                }

                const url = URL.createObjectURL(blob);
                blobUrlRef.current = url;
                setBlobUrl(url);
                setIsLoading(false);
            })
            .catch((err) => {
                setError(`Ошибка: ${err.message}`);
                setIsLoading(false);
            });

        return () => {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = '';
            }
        };
    }, [pdf_path, hasCustomLoader, use_image_fallback]);

    useEffect(() => {
        const updateWidth = () => {
            const width = window.innerWidth;

            if (width < 480) {
                setPageWidth(width - 24);

            } else if (width < 768) {
                setPageWidth(width - 32);

            } else {
                setPageWidth(Math.min(900, width - 80));
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);

        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    useEffect(() => {
        if (use_image_fallback || !numPages) {
            return;
        }

        if (visible_pages_count >= numPages) {
            return;
        }

        const handle = requestAnimationFrame(() => {
            set_visible_pages_count((previous_count) => (
                Math.min(
                    previous_count + PAGES_BATCH_SIZE,
                    numPages,
                )
            ));
        });

        return () => cancelAnimationFrame(handle);
    }, [numPages, visible_pages_count, use_image_fallback]);

    if (!pdf_path && !hasCustomLoader) {
        return <div className="PDFViewer-error">PDF не найден</div>;
    }

    if (isLoading) {
        return (
            <div className="PDFViewer-loading flex-column gap-small">
                <LoadingIndicator label="Ожидание PDF" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="PDFViewer-error">
                {error}
                {blobUrl && (
                    <div className="PDFViewer-error-link">
                        <a href={blobUrl} target="_blank" rel="noreferrer">
                            Открыть PDF в новой вкладке
                        </a>
                    </div>
                )}
            </div>
        );
    }

    if (use_image_fallback) {
        return (
            <div className="PDFViewer-container">
                <img
                    className="PDFViewer-webview-image"
                    src={blobUrl}
                    alt="Предпросмотр PDF"
                />
            </div>
        );
    }

    return (
        <div className="PDFViewer-container">
            <Document
                file={blobUrl}
                options={PDF_OPTIONS}
                onLoadSuccess={({ numPages: loaded_pages_count }) => {
                    setNumPages(loaded_pages_count);
                    set_visible_pages_count(0);
                    setError('');
                }}
                onLoadError={() => {
                    setError(
                        'Не удалось отобразить PDF. ' +
                        'Откройте файл в новой вкладке.'
                    );
                }}
                loading={(
                    <div className="PDFViewer-loading flex-column gap-small">
                        <LoadingIndicator label="Рендеринг PDF" />
                    </div>
                )}
                error=""
                noData="Нет данных PDF"
            >
                {Array.from(
                    new Array(visible_pages_count),
                    (_, index) => (
                        <div
                            key={`page_${index + 1}`}
                            className="PDFViewer-page"
                        >
                            <Page
                                pageNumber={index + 1}
                                width={pageWidth}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        </div>
                    ),
                )}
            </Document>
        </div>
    );
};


export default PDFViewer;
